/**
 * Short-window dedupe for back-to-back duplicate tracking-categories fetches
 * (parent OrganisationDetails effect + transactions-tab fallback). The single
 * source of truth for "tracking categories are loaded" lives on the
 * organisation object (`trackingCategories !== undefined` after a successful
 * fetch). Failed fetches are NOT stored as `[]`, so a delayed retry can still
 * run; this guard caps that retry so a persistent error does not loop.
 */
const trackingCategoriesBurstAt = new Map<string, number>();
const trackingCategoriesInFlight = new Set<string>();
const trackingCategoriesFailCount = new Map<string, number>();

const DEFAULT_BURST_MS = 1500;
const MAX_RETRY_DELAY_MS = 30000;

function pruneBurstMap(now: number, windowMs: number): void {
  if (trackingCategoriesBurstAt.size <= 200) return;
  for (const [key, at] of trackingCategoriesBurstAt.entries()) {
    if (now - at > windowMs * 10) trackingCategoriesBurstAt.delete(key);
  }
}

export function beginTrackingCategoriesFetch(organisationId: string): boolean {
  if (trackingCategoriesInFlight.has(organisationId)) return false;
  trackingCategoriesInFlight.add(organisationId);
  return true;
}

export function endTrackingCategoriesFetch(organisationId: string): void {
  trackingCategoriesInFlight.delete(organisationId);
}

export function markTrackingCategoriesFetchSucceeded(
  organisationId: string
): void {
  trackingCategoriesFailCount.delete(organisationId);
}

export function markTrackingCategoriesFetchFailed(organisationId: string): void {
  trackingCategoriesFailCount.set(
    organisationId,
    (trackingCategoriesFailCount.get(organisationId) ?? 0) + 1
  );
}

/** Delay before a follow-up fetch when categories are still missing. */
export function getTrackingCategoriesRetryDelayMs(
  organisationId: string
): number {
  const failCount = trackingCategoriesFailCount.get(organisationId) ?? 0;
  if (failCount <= 0) return DEFAULT_BURST_MS + 100;
  return Math.min(MAX_RETRY_DELAY_MS, DEFAULT_BURST_MS * 2 ** failCount);
}

export function shouldSkipTrackingCategoriesBurst(
  organisationId: string,
  windowMs = DEFAULT_BURST_MS
): boolean {
  if (trackingCategoriesInFlight.has(organisationId)) return true;
  const now = Date.now();
  const lastAt = trackingCategoriesBurstAt.get(organisationId);
  const failCount = trackingCategoriesFailCount.get(organisationId) ?? 0;
  const skipWindowMs =
    failCount > 0
      ? Math.min(MAX_RETRY_DELAY_MS, windowMs * 2 ** failCount)
      : windowMs;
  if (lastAt != null && now - lastAt < skipWindowMs) return true;
  trackingCategoriesBurstAt.set(organisationId, now);
  pruneBurstMap(now, windowMs);
  return false;
}
